import { useState } from 'react';
import { ROLE_META, S, COMPANY_SETTINGS } from '../../utils/constants';
import { Icon } from '../common/Icon';

/**
 * Shell Layout Component
 * Main app wrapper with sidebar navigation and mobile header
 */
export function Shell({ currentUser, page, setPage, onLogout, children }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const rm = ROLE_META[currentUser.role];

  // Build navigation items based on role
  const navItems = [
    // Dashboard (All users)
    { key: "dashboard", icon: "📊", label: "لوحة التحكم" },
    
    // Contacts - Only for approved users or admin
    ...((currentUser.role === 'admin' || currentUser.is_approved_to_work)
      ? [{ key: "contacts", icon: "🍽️", label: "جهات الاتصال" }]
      : []),
    
    // My Contacts - For marketers only (shows their claimed contacts)
    ...(currentUser.role === 'marketer'
      ? [{ key: "my-contacts", icon: "📋", label: "جهات اتصالى" }]
      : []),
    
    // GoSnel Maps - All approved users and admin (coming soon)
    ...((currentUser.role === 'admin' || currentUser.is_approved_to_work)
      ? [{ key: "maps", icon: "🗺️", label: "GoSnel Maps", disabled: true, soonLabel: "قريباً" }]
      : []),
    
    // Archive - Admin ONLY (shows archived restaurants)
    ...(currentUser.role === 'admin'
      ? [{ key: "archive", icon: "📦", label: "الأرشيف" }]
      : []),
    
    // Learning - Always visible (for unapproved users)
    { key: "learning", icon: "📚", label: "التدريب" },
    
    // Commission pages (Marketer & Rep)
    ...((currentUser.role === "marketer" || currentUser.role === "rep")
      ? [{ key: "wallet", icon: "💰", label: "محفظتي" }]
      : []),
    
    // Financial reports (Admin ONLY - Sales Manager cannot access)
    ...(currentUser.role === "admin"
      ? [{ key: "financial", icon: "📊", label: "التقارير المالية" }]
      : []),
    
    // KPI & Performance (Admin ONLY - Sales Manager has own dashboard)
    ...(currentUser.role === "admin"
      ? [{ key: "kpi", icon: "📈", label: "مؤشرات الأداء" }]
      : []),
    
    // AI Training (All users)
    { key: "ai-training", icon: "🎓", label: "التدريب الذكي" },
    
    // Shared Documents (All users)
    { key: "documents", icon: "📁", label: "المستندات المشتركة" },
    
    // Training KPI (Admin ONLY)
    ...(currentUser.role === "admin"
      ? [{ key: "training-kpi", icon: "📊", label: "تدريب الفريق" }]
      : []),
    
    // Settings (Admin ONLY - Sales Manager cannot access)
    ...(currentUser.role === "admin"
      ? [{ key: "settings", icon: "⚙️", label: "الإعدادات" }]
      : []),
  ];

  return (
    <div style={{
      display: "flex",
      minHeight: "100vh",
      direction: "rtl",
      fontFamily: "'Cairo',sans-serif",
      background: S.bg,
      color: S.txt
    }}>
      <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;900&display=swap" rel="stylesheet"/>
      
      {/* Global Styles */}
      <style>{`
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        ::-webkit-scrollbar {
          width: 4px;
        }
        ::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        @keyframes sIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        @media (max-width: 768px) {
          .sidebar-desktop {
            display: none !important;
          }
          .mobile-header {
            display: flex !important;
          }
        }
      `}</style>
      
      {/* Mobile Header */}
      <div className="mobile-header" style={{
        display: "none",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: 56,
        background: "#ffffff",
        borderBottom: "1px solid #e2e8f0",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 16px",
        zIndex: 100
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Burger Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: 8,
              display: "flex",
              flexDirection: "column",
              gap: 4
            }}
          >
            <span style={{ display: "block", width: 20, height: 2, background: "#0f172a", borderRadius: 2 }}></span>
            <span style={{ display: "block", width: 20, height: 2, background: "#0f172a", borderRadius: 2 }}></span>
            <span style={{ display: "block", width: 20, height: 2, background: "#0f172a", borderRadius: 2 }}></span>
          </button>
          
          {COMPANY_SETTINGS.companyLogo ? (
            <img 
              src={COMPANY_SETTINGS.companyLogo} 
              alt="logo" 
              style={{
                height: 40,
                width: 40,
                borderRadius: "50%",
                objectFit: "cover",
                border: "2px solid #e2e8f0"
              }} 
              onError={e => { e.target.style.display = "none"; }}
            />
          ) : (
            <span style={{ color: "#0f172a", fontWeight: 900, fontSize: 14 }}>
              {COMPANY_SETTINGS.companyName}
            </span>
          )}
        </div>
        <button 
          onClick={onLogout} 
          style={{
            background: "transparent",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            padding: "6px 12px",
            color: "#64748b",
            cursor: "pointer",
            fontSize: 12,
            fontFamily: "'Cairo',sans-serif"
          }}
        >
          خروج
        </button>
      </div>
      
      {/* Desktop Sidebar */}
      <div className="sidebar-desktop" style={{
        width: 228,
        background: "#ffffff",
        borderLeft: "1px solid #e2e8f0",
        display: "flex",
        flexDirection: "column",
        padding: "20px 14px",
        flexShrink: 0
      }}>
        {/* Logo */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 26,
          paddingBottom: 18,
          borderBottom: "1px solid #e2e8f0"
        }}>
          {COMPANY_SETTINGS.companyLogo ? (
            <img 
              src={COMPANY_SETTINGS.companyLogo} 
              alt="logo" 
              style={{
                height: 48,
                width: 48,
                borderRadius: "50%",
                objectFit: "cover",
                border: "2px solid #e2e8f0"
              }} 
              onError={e => { e.target.style.display = "none"; }}
            />
          ) : (
            <span style={{ color: "#000000", fontWeight: 900, fontSize: 15 }}>
              {COMPANY_SETTINGS.companyName}
            </span>
          )}
        </div>

        {/* Navigation */}
        <nav style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 4
        }}>
          {navItems.map(item => (
            item.disabled ? (
              <div
                key={item.key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 14px",
                  borderRadius: 10,
                  border: "none",
                  background: "transparent",
                  color: "#94a3b8",
                  cursor: "not-allowed",
                  fontFamily: "'Cairo',sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  textAlign: "right",
                  opacity: 0.5,
                }}
              >
                {item.icon} {item.label}
                <span style={{
                  marginLeft: "auto",
                  background: "#e2e8f0",
                  color: "#94a3b8",
                  padding: "2px 8px",
                  borderRadius: 10,
                  fontSize: 10,
                  fontWeight: 700,
                }}>
                  {item.soonLabel || "قريباً"}
                </span>
              </div>
            ) : (
              <button 
                key={item.key} 
                onClick={() => setPage(item.key)} 
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 14px",
                  borderRadius: 10,
                  border: "none",
                  background: page === item.key ? "#f1f5f9" : "transparent",
                  color: page === item.key ? "#4ecdc4" : "#64748b",
                  cursor: "pointer",
                  fontFamily: "'Cairo',sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  textAlign: "right",
                  borderRight: page === item.key 
                    ? `3px solid ${COMPANY_SETTINGS.primaryColor}` 
                    : "3px solid transparent",
                  transition: "all 0.15s"
                }}
              >
                {item.icon} {item.label}
              </button>
            )
          ))}
        </nav>

        {/* User Card */}
        <div style={{
          borderTop: "1px solid #e2e8f0",
          paddingTop: 14
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 10
          }}>
            <div style={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              background: rm.color + "22",
              border: `1px solid ${rm.color}44`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 15,
              flexShrink: 0
            }}>
              <Icon name={rm.icon} size={20} color={rm.color} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{
                color: "#0f172a",
                fontSize: 12,
                fontWeight: 700,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}>
                {currentUser.name}
              </div>
              <div style={{ color: rm.color, fontSize: 10, marginTop: 1 }}>
                {rm.label}
              </div>
            </div>
          </div>
          <button 
            onClick={onLogout} 
            style={{
              width: "100%",
              padding: "7px 0",
              background: "transparent",
              border: "1px solid #e2e8f0",
              borderRadius: 9,
              color: "#64748b",
              cursor: "pointer",
              fontFamily: "'Cairo',sans-serif",
              fontSize: 12
            }}
          >
            ↩ تسجيل الخروج
          </button>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      {mobileMenuOpen && (
        <>
          {/* Overlay */}
          <div
            onClick={() => setMobileMenuOpen(false)}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              zIndex: 200
            }}
          />
          
          {/* Drawer */}
          <div style={{
            position: "fixed",
            top: 0,
            right: 0,
            width: 280,
            height: "100vh",
            background: "#ffffff",
            borderLeft: "1px solid #e2e8f0",
            zIndex: 201,
            overflowY: "auto",
            padding: "20px 16px",
            animation: "sIn 0.2s ease"
          }}>
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ color: "#0f172a", fontSize: 16, marginBottom: 4 }}>{COMPANY_SETTINGS.companyName}</h3>
              <p style={{ color: "#64748b", fontSize: 12 }}>{currentUser.name} - {rm.label}</p>
            </div>
            
            {/* Nav Items */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 24 }}>
              {navItems.map(item => (
                item.disabled ? (
                  <div
                    key={item.key}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      background: "transparent",
                      border: "1px solid transparent",
                      borderRadius: 10,
                      textAlign: "right",
                      cursor: "not-allowed",
                      fontFamily: "'Cairo',sans-serif",
                      fontSize: 13,
                      color: "#94a3b8",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      opacity: 0.5,
                    }}
                  >
                    <span style={{ fontSize: 16 }}>{item.icon}</span>
                    <span>{item.label}</span>
                    <span style={{
                      marginLeft: "auto",
                      background: "#e2e8f0",
                      color: "#94a3b8",
                      padding: "2px 8px",
                      borderRadius: 10,
                      fontSize: 10,
                      fontWeight: 700,
                    }}>
                      {item.soonLabel || "قريباً"}
                    </span>
                  </div>
                ) : (
                  <button
                    key={item.key}
                    onClick={() => {
                      setPage(item.key);
                      setMobileMenuOpen(false);
                    }}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      background: page === item.key ? "#f1f5f9" : "transparent",
                      border: page === item.key ? "1px solid #e2e8f0" : "1px solid transparent",
                      borderRadius: 10,
                      textAlign: "right",
                      cursor: "pointer",
                      fontFamily: "'Cairo',sans-serif",
                      fontSize: 13,
                      color: page === item.key ? rm.color : "#64748b",
                      fontWeight: page === item.key ? 700 : 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 8
                    }}
                  >
                    <span style={{ fontSize: 16 }}>{item.icon}</span>
                    <span>{item.label}</span>
                  </button>
                )
              ))}
            </div>
            
            {/* Logout */}
            <button
              onClick={() => {
                onLogout();
                setMobileMenuOpen(false);
              }}
              style={{
                width: "100%",
                padding: "10px 0",
                background: "transparent",
                border: "1px solid #e2e8f0",
                borderRadius: 9,
                color: "#ef4444",
                cursor: "pointer",
                fontFamily: "'Cairo',sans-serif",
                fontSize: 13,
                fontWeight: 700
              }}
            >
              ↩ تسجيل الخروج
            </button>
          </div>
        </>
      )}

      {/* Main Content */}
      <main className="main-content" style={{
        flex: 1,
        padding: "26px 28px",
        overflowX: "hidden",
        marginTop: 0
      }}>
        {children}
      </main>
    </div>
  );
}